import { ToastQueue } from "@react-spectrum/toast";
import { NewMailRequest } from "../../services/soap/mail/requests/NewMailRequest";
import { NewMessageRequest } from "../../services/soap/message/requests/NewMessageRequest";
import { NewMessageTemplateRequest } from "../../services/soap/message/requests/NewMessageTemplateRequest";
import { NewMessageFormData } from "../message/useNewMessageForm";
import { UploadedDocument } from "../message/useNewMessageFiles";

/**
 * Message type constants
 */
export const MESSAGE_TYPES = {
  NOTE: "1",
  PHONE: "6",
  EMAIL: "7",
  MEETING: "12",
} as const;

/**
 * Configuration for message operations
 */
export interface MessageOperationsConfig {
  mailService: any;
  messageService: any;
  serverService: any;
  sessionId: string;
  contextId: any;
  messageTypes: Array<{ id: string; type: string }>;
  /** Parent message ID for replies */
  parentMessageId?: string;
  /** Message group ID from original message (for replies) */
  messageGroupId?: string;
  /** Tree handle from original message (for replies) */
  treeHandle?: string;
  /** User's default message group (from session.sessionInfo.msgGroup) - for personal messages */
  userMessageGroup?: string;
  /** User's default message tree handle (from session.sessionInfo.msgNode) - for personal messages */
  userMessageTreeHandle?: string;
  /** @deprecated - no longer used, context is determined by contextId presence */
  isFolderContext?: boolean;
}

/**
 * Result of a message operation
 */
export interface MessageOperationResult {
  success: boolean;
  messageId?: string;
  error?: string;
}

/**
 * Encode HTML special characters for safe transmission
 */
export const encodeHTML = (str: string): string => {
  if (!str) return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

/**
 * Build complete HTML document from content
 */
export const buildCompleteHtml = (content: string): string => {
  return `<!DOCTYPE html>
<html>
<head>
<style type="text/css">
body,td,th { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #333333; }
a:link { color: #666666; }
a:visited { color: #999999; }
</style>
</head>
<body>
${content}
</body>
</html>`;
};

/**
 * Extract email address from format "Name <email@example.com>"
 */
export const extractEmail = (email: string): string => {
  const match = /<(.+?)>/.exec(email);
  return match ? match[1] : email;
};

/**
 * Validate email address format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(extractEmail(email));
};

/**
 * Truncate text to specified length
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (!text) return "";
  return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

/**
 * Validate email form data
 * @returns Error message or null if valid
 */
export const validateEmailForm = (
  formState: NewMessageFormData,
): string | null => {
  if (formState.to.length === 0) {
    return "Please enter the recipient email";
  }

  const invalidEmails = formState.to.filter(
    (item: any) => !isValidEmail(item.emailText),
  );

  if (invalidEmails.length > 0) {
    return "Please enter valid email addresses";
  }

  if (!formState.subject?.trim()) {
    return "Please enter the email subject";
  }

  return null;
};

/**
 * Validate message form data (for note, phone, meeting types)
 * @returns Error message or null if valid
 */
export const validateMessageForm = (
  formState: NewMessageFormData,
  messageType: string,
): string | null => {
  // Meeting type requires participants
  if (
    messageType === MESSAGE_TYPES.MEETING &&
    formState.participants.length === 0
  ) {
    return "Please add meeting participants";
  }

  return null;
};

/**
 * Send an email message
 *
 * Context handling (per client recommendation):
 * 1. REPLY: Use group + treeHandle + parent from original message (NO contextId)
 * 2. NEW MESSAGE WITH CONTEXT: Use contextId ONLY (NO group + treeHandle)
 * 3. PERSONAL MESSAGE (no context): Use group + treeHandle from user settings
 */
export const sendEmailMessage = async (
  config: MessageOperationsConfig,
  formState: NewMessageFormData,
  documentIds: UploadedDocument[],
  emailContent: string,
): Promise<MessageOperationResult> => {
  try {
    // Validate form
    const validationError = validateEmailForm(formState);
    if (validationError) {
      ToastQueue.negative(validationError, { timeout: 3000 });
      return { success: false, error: validationError };
    }

    const toEmails = formState.to.map((item: any) => item.emailText);
    const ccEmails = formState.cc.map((item: any) => item.emailText);
    const encodedContent = encodeHTML(buildCompleteHtml(emailContent));

    const emailParams: any = {
      priority: "2",
      html: "1",
      from: formState.fromValue?.id,
      mailboxId: formState.fromValue?.id,
      to: toEmails,
      notify: formState.notify,
      subject: formState.subject,
      body: encodedContent,
      messageType: formState.messageType,
    };

    // Handle context/group parameters based on client recommendation:
    // Priority order:
    // 1. Reply: Use group + treeHandle from original message (NO contextId)
    // 2. New message with context: Use contextId ONLY (NO group + treeHandle)
    // 3. Personal message (no context): Use group + treeHandle from user settings

    if (config.parentMessageId && config.messageGroupId && config.treeHandle) {
      emailParams.group = config.messageGroupId;
      emailParams.treeHandle = config.treeHandle;
      emailParams.parent = config.parentMessageId;
    } else if (config.contextId && config.isFolderContext) {
      emailParams.contextId = config.contextId;
    } else if (config.userMessageGroup && config.userMessageTreeHandle) {
      emailParams.group = config.userMessageGroup;
      emailParams.treeHandle = config.userMessageTreeHandle;
    } else {
      emailParams.treeHandle = config.contextId;
    }

    // Add CC if present
    if (ccEmails.length > 0) {
      emailParams.cc = ccEmails;
    }

    // Add attachments if present
    if (documentIds.length > 0) {
      emailParams.attachments = {
        attachment: documentIds.map((doc) => ({
          id: doc.id,
          name: doc.name,
          size: doc.size,
        })),
      };
    }

    const response = await config.mailService.newMail(
      new NewMailRequest(config.serverService, config.sessionId, emailParams),
    );

    if (response?.result === "OK") {
      ToastQueue.positive("Email sent successfully", { timeout: 3000 });
      return { success: true, messageId: response.newId };
    } else {
      const errorMessage =
        response?.EXCEPTION?.message || "Failed to send email";
      ToastQueue.negative(errorMessage, { timeout: 3000 });
      return { success: false, error: errorMessage };
    }
  } catch (error: any) {
    console.error("Error sending email:", error);
    const errorMessage = error?.message || "Error sending email";
    ToastQueue.negative(errorMessage, { timeout: 3000 });
    return { success: false, error: errorMessage };
  }
};

/**
 * Send a message (note, phone call, meeting)
 *
 * Context handling (per client recommendation):
 * 1. REPLY: Use group + treeHandle + parent from original message (NO contextId)
 * 2. NEW MESSAGE WITH CONTEXT: Use contextId ONLY (NO group + treeHandle)
 * 3. PERSONAL MESSAGE (no context): Use group + treeHandle from user settings
 */
export const sendOtherMessage = async (
  config: MessageOperationsConfig,
  formState: NewMessageFormData,
  documentIds: UploadedDocument[],
  messageContent: string,
): Promise<MessageOperationResult> => {
  try {
    // Validate form
    const validationError = validateMessageForm(
      formState,
      formState.messageType,
    );
    if (validationError) {
      ToastQueue.negative(validationError, { timeout: 3000 });
      return { success: false, error: validationError };
    }

    // Find custom type ID
    const customType = config.messageTypes.find(
      (item: any) => item.type === formState.messageType,
    )?.id;

    const toRecipients = formState.to.map((item: any) => ({
      id: item.id,
      CONTENT: item.contactName,
    }));

    const contactRelations = formState.participants.map((item: any) => ({
      type: "0",
      identifier: "",
      contact: item.id,
    }));

    const encodedContent = encodeHTML(buildCompleteHtml(messageContent));

    const messageParams: any = {
      numberOfAttachments: documentIds.length,
      priority: "2",
      customType: customType,
      type: formState.messageType,
      status: formState.phoneStatus,
      SUBJECT: { string: formState.subject || "" },
      RECIPIENT: { USER: formState.notify, TO: toRecipients },
      COMMENT: { string: encodedContent },
      HTML: { string: encodedContent },
      DOCUMENT: {
        ITEM: documentIds.map((doc) => ({
          id: doc.id,
          name: doc.name,
          size: doc.size,
        })),
      },
      CONTACT_RELATIONS: { CONTACT_RELATION: contactRelations },
    };

    // Handle context/group parameters based on client recommendation:
    // Priority order:
    // 1. Reply: Use group + treeHandle from original message (NO contextId)
    // 2. New message with context: Use contextId ONLY (NO group + treeHandle)
    // 3. Personal message (no context): Use group + treeHandle from user settings

    if (config.parentMessageId && config.messageGroupId && config.treeHandle) {
      // REPLY: Use group + treeHandle from the original message
      // Do NOT use contextId - use the exact group/treeHandle from the message being replied to
      messageParams.group = config.messageGroupId;
      messageParams.treeHandle = config.treeHandle;
      messageParams.parent = config.parentMessageId;
    } else if (config.contextId) {
      // NEW MESSAGE WITH CONTEXT: Use contextId ONLY
      // Server will resolve the proper group and treeHandle internally
      // Do NOT send group or treeHandle when using contextId
      messageParams.contextId = config.contextId;
    } else if (config.userMessageGroup && config.userMessageTreeHandle) {
      // PERSONAL MESSAGE (no context): Use user's default group + treeHandle
      messageParams.group = config.userMessageGroup;
      messageParams.treeHandle = config.userMessageTreeHandle;
    } else {
      // Error: Cannot determine where to send the message
      const errorMessage =
        "Cannot send message: No valid context or user message settings available";
      console.error(errorMessage, {
        contextId: config.contextId,
        userMessageGroup: config.userMessageGroup,
        userMessageTreeHandle: config.userMessageTreeHandle,
        parentMessageId: config.parentMessageId,
        messageGroupId: config.messageGroupId,
        treeHandle: config.treeHandle,
      });
      ToastQueue.negative(errorMessage, { timeout: 3000 });
      return { success: false, error: errorMessage };
    }

    const response = await config.messageService.newMessage(
      new NewMessageRequest(
        config.serverService,
        config.sessionId,
        messageParams,
      ),
    );

    if (response?.result === "OK") {
      ToastQueue.positive("Message sent successfully", { timeout: 3000 });
      return { success: true, messageId: response.newId };
    } else {
      const errorMessage =
        response?.EXCEPTION?.message || "Failed to send message";
      ToastQueue.negative(errorMessage, { timeout: 3000 });
      return { success: false, error: errorMessage };
    }
  } catch (error: any) {
    console.error("Error sending message:", error);
    const errorMessage = error?.message || "Error sending message";
    ToastQueue.negative(errorMessage, { timeout: 3000 });
    return { success: false, error: errorMessage };
  }
};

/**
 * Save message as draft
 */
export const saveDraftMessage = async (
  config: MessageOperationsConfig,
  formState: NewMessageFormData,
  documentIds: UploadedDocument[],
  messageContent: string,
): Promise<MessageOperationResult> => {
  try {
    // For email drafts, validate recipient
    if (
      formState.messageType === MESSAGE_TYPES.EMAIL &&
      formState.to.length === 0
    ) {
      ToastQueue.negative("Please enter the recipient email", {
        timeout: 3000,
      });
      return { success: false, error: "Please enter the recipient email" };
    }

    const messageTemplate = {
      contextId: config.contextId,
      templateName: formState.subject || "[ No Subject ]",
      description: "",
      created: new Date().toISOString(),
      subject: formState.subject || "[ No Subject ]",
      isDraft: 1,
      html: 0,
      type: formState.messageType,
      language: "",
      isDefault: 0,
      folderId: "",
      projectId: "",
      activityId: "",
      RECIPIENT: {
        TO: formState.to,
        CC: formState.cc,
        USER: formState.notify,
      },
      BODY: {
        string: buildCompleteHtml(messageContent),
      },
      DOCUMENTS: {
        DOCUMENT: documentIds.map((doc) => ({
          id: doc.id,
          name: doc.name,
          size: doc.size,
        })),
      },
      TRANSLATIONS: "",
    };

    const response = await config.messageService.newMessageTemplate(
      new NewMessageTemplateRequest(config.serverService, config.sessionId, {
        messageTemplate: messageTemplate,
      }),
    );

    if (response?.result === "OK") {
      ToastQueue.positive("Draft saved successfully", { timeout: 3000 });
      return { success: true, messageId: response.newId };
    } else {
      const errorMessage =
        response?.EXCEPTION?.message || "Failed to save draft";
      ToastQueue.negative(errorMessage, { timeout: 3000 });
      return { success: false, error: errorMessage };
    }
  } catch (error: any) {
    console.error("Error saving draft:", error);
    const errorMessage = error?.message || "Error saving draft";
    ToastQueue.negative(errorMessage, { timeout: 3000 });
    return { success: false, error: errorMessage };
  }
};

/**
 * Determine if message type is email
 */
export const isEmailType = (messageType: string): boolean => {
  return String(messageType) === MESSAGE_TYPES.EMAIL;
};

/**
 * Send message based on type
 * Automatically routes to sendEmailMessage or sendOtherMessage
 */
export const sendMessage = async (
  config: MessageOperationsConfig,
  formState: NewMessageFormData,
  documentIds: UploadedDocument[],
  messageContent: string,
): Promise<MessageOperationResult> => {
  if (isEmailType(formState.messageType)) {
    return sendEmailMessage(config, formState, documentIds, messageContent);
  } else {
    return sendOtherMessage(config, formState, documentIds, messageContent);
  }
};
